import { AccountsService, PlanName } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { navigateInternal } from 'base/navigation';
import { Avatar } from 'base/ui/avatar';
import { Button } from 'base/ui/button';
import { IconBeta, IconChevronRight, IconGift } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { mount } from 'base/ui/overlay';
import { DividerLine } from 'base/ui/panel';
import { ToastQueue } from 'base/ui/toast';
import { COLOR_TOKENS } from 'base/ui/tokens';
import { TextRegular, TextSmall } from 'base/ui/typography';
import { TeamFeatureGateDialog } from 'blocks/billing/feature_gate_dialog';
import { getHumanPlanName } from 'blocks/billing/plan_utils';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { ReferralEarningDialog } from 'blocks/referral_earning';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import createTeamImage from './create-team.file.svg';
import figmaLibraryImage from './figma-library.file.svg';
import { TeamAvatar } from './projects_list/projects_list';
import reactLibraryImage from './react-library.file.svg';
import { DashboardRoute, dashboardRouteToUrl } from './routes';
import styles from './sidebar.css';
import siteBuilderImage from './site-builder.file.svg';
import webflowLibraryImage from './webflow-library.file.svg';

export const Sidebar = observer(function _Sidebar({
  route,
  toastQueue,
  accountStore,
  featureFlagsStore,
  accountsService,
  analytics,
}: {
  route: DashboardRoute;
  toastQueue: ToastQueue;
  featureFlagsStore: FeatureFlagsStore;
  accountStore: AccountStore;
  accountsService: AccountsService;
  analytics: AnalyticsService;
}) {
  const me = accountStore.member;
  const teamEnabled = me?.productRestrictions.teamEnabled === true;
  const teamRouteActive =
    route.path === 'dashboard' && route.projectsFilter === 'team';

  const badge = {
    slot: teamEnabled ? 'team' : 'personal',
    label: getHumanPlanName(accountStore.member?.planName ?? PlanName.FREE),
    color:
      accountStore.member?.planName === PlanName.FREE ? 'grey' : 'gradient',
  } as const;

  const openUpgradeDialog = () => {
    const close = mount(
      <TeamFeatureGateDialog onClose={() => close()} analytics={analytics} />,
    );
  };

  const openReferralEarningDialog = () => {
    analytics.track({
      event: 'referral_flow:invite_and_earn_button_clicked',
      location: 'dashboard',
    });
    const close = mount(
      <ReferralEarningDialog
        analyticsService={analytics}
        toastQueue={toastQueue}
        onClose={() => close()}
        accountsService={accountsService}
      />,
    );
  };

  return (
    <div className={styles.sidebarContainer}>
      <Rows spacing={8}>
        <SidebarLink
          label="Site Builder"
          iconUrl={siteBuilderImage}
          href={dashboardRouteToUrl({ path: 'dashboard' })}
          isInternal
          active={
            route.path === 'dashboard' && route.projectsFilter === undefined
          }
        />
        <SidebarLink
          label="Personal"
          badge={
            badge.slot === 'personal' ? (
              <TextSmall tag="span" color={badge.color}>
                {badge.label}
              </TextSmall>
            ) : undefined
          }
          iconNode={
            <Avatar
              size="small"
              {...me}
              color={me?.color || COLOR_TOKENS.colorGrey}
            />
          }
          href={dashboardRouteToUrl({
            path: 'dashboard',
            projectsFilter: 'user',
          })}
          isInternal
          active={route.path === 'dashboard' && route.projectsFilter === 'user'}
          isSecondary
          level={2}
        />
        {teamEnabled || teamRouteActive ? (
          <SidebarLink
            label={me?.team.name || 'Team'}
            badge={
              me?.team.name && badge.slot === 'team' ? (
                <TextSmall color={badge.color}>{badge.label}</TextSmall>
              ) : undefined
            }
            iconNode={<TeamAvatar size="small" team={me?.team} />}
            href={dashboardRouteToUrl({
              path: 'dashboard',
              projectsFilter: 'team',
            })}
            isInternal
            active={
              route.path === 'dashboard' && route.projectsFilter === 'team'
            }
            isSecondary
            level={2}
          />
        ) : (
          <SidebarLink
            label="Create a team"
            iconUrl={createTeamImage}
            onClick={openUpgradeDialog}
            level={2}
          />
        )}
        <DividerLine />
        <SidebarLink
          label="Figma Library"
          iconUrl={figmaLibraryImage}
          href="/figma-library"
        />
        <DividerLine />
        <SidebarLink
          label="Webflow Library"
          iconUrl={webflowLibraryImage}
          href="/components"
          expandsRight
        />
        <DividerLine />
        <SidebarLink
          label="React Library"
          iconUrl={reactLibraryImage}
          href="react/components"
          expandsRight
        />
      </Rows>
      {featureFlagsStore.get('enableReferral') && (
        <Button
          xalign="left"
          size="large"
          variant="tertiary"
          icon={<IconGift />}
          label="Invite & earn"
          onClick={openReferralEarningDialog}
        />
      )}
    </div>
  );
});

const SidebarLink = function SidebarLink({
  label,
  betaTag,
  badge,
  iconUrl,
  iconNode,
  href,
  onClick,
  active,
  isInternal,
  level = 1,
  isSecondary,
  expandsRight,
}: {
  label: string;
  betaTag?: boolean;
  badge?: React.ReactNode;
  iconUrl?: string;
  iconNode?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  isInternal?: boolean;
  level?: 1 | 2;
  isSecondary?: boolean;
  expandsRight?: boolean;
}) {
  const Text = level === 1 ? TextRegular : TextSmall;
  return (
    <Button
      size="large"
      variant={isSecondary ? 'subtle' : 'tertiary'}
      background={active ? 'grey2' : 'transparent'}
      subtlyDisabled={active}
      href={href}
      onClick={onClick ?? (isInternal ? navigateInternal : undefined)}
    >
      <div className={styles.link}>
        <div
          className={classNames(
            styles.linkIcon,
            level === 2 && styles.isLevel2,
          )}
        >
          {iconUrl && <img src={iconUrl} className={styles.linkIconImage} />}
          {iconNode}
        </div>
        <div className={styles.textAndBetaTag}>
          <Text lineClamp={1}>{label}</Text>
          {betaTag && (
            <div className={styles.betaTag}>
              <IconBeta />
            </div>
          )}
        </div>
        {badge && <div className={styles.linkBadge}>{badge}</div>}
        {expandsRight && (
          <div className={styles.linkExpandsRight}>
            <IconChevronRight />
          </div>
        )}
      </div>
    </Button>
  );
};
